import React, { useEffect, useState } from "react";
import { Link, NavLink, useSearchParams } from "react-router-dom";
import logo from "../images/sri-logo.png";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInfo, initializeAuth, logoutUser, selectAppRoles } from "../redux/userSlice";
import { FaBell } from "react-icons/fa";
import { Modal, Button, NavDropdown } from "react-bootstrap";
import AuctionList from "./AuctionList";
import "./Navbar.css";

const Navbar = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const notifications = userInfo.notifications || [];
  const isAuthenticated = userInfo.user && userInfo.user.email ? true : false;
  const isLoggedIn = Object.keys(userInfo.user).length > 0 ? true : false;
  const appRoles = useSelector(selectAppRoles) || {};

  const [, setIsHoveredLogin] = useState(false);
  const [, setIsHoveredLogout] = useState(false);
  const [isAdminOrSriUser, setIsAdminOrSriUser] = useState(false);

  // On initial load/redirect, if there is a search param we set the initial state to open
  const [showAuctionModal, setShowAuctionModal] = useState(searchParams.get("modal") === "auctionList");

  // Initialize authentication on mount.
  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  // Reinitialize auth if profile is updated.
  useEffect(() => {
    if (userInfo.profileUpdated) {
      dispatch(initializeAuth());
    }
  }, [userInfo.profileUpdated, dispatch]);

  // Flip the admin flag on whenever the user is logged in and has an admin
  // role in the redux app-roles list. The logic was previously a separate
  // setAdminRole() function called from inside this effect, but inlining it
  // here makes the effect's true dependencies obvious (isLoggedIn, appRoles)
  // — no function-identity flicker, no over-listed deps like dispatch/userInfo.
  //
  // Uses .some() to short-circuit on the first matching role and to avoid
  // calling setIsAdminOrSriUser more than once per run.
  //
  // TODO: reset isAdminOrSriUser to false on logout — currently the flag
  // stays true after a user logs out until the component unmounts, which
  // can leak the "Submit a Ticket" admin link to the next session.
  useEffect(() => {
    if (isLoggedIn) {
      setAdminRole();
    }
  }, [dispatch, userInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAdminRole = () => {
    Object.values(appRoles).forEach((role) => {
      if (role === "Portal Admin" || role === "Portal SRI User") {
        setIsAdminOrSriUser(true);
      }
    });
  };

  // Function to update both state and search param at the same time
  const handleOpenAuctionModal = () => {
    // Set search param 'modal' to 'auctionList'
    const newParams = new URLSearchParams(searchParams);
    newParams.append("modal", "auctionList");
    setSearchParams(newParams);

    // Set showAuctionModal state to true
    setShowAuctionModal(true);
  };

  const handleCloseAuctionModal = () => {
    // Delete search param 'modal'
    console.log({ URL: window.location });

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("modal");
    setSearchParams(newParams);

    // Set showAuctionModal state to false
    setShowAuctionModal(false);
    console.log({ URL: window.location });
  };

  const pendingNotificationsCount = notifications.filter((n) => !n.is_read).length;
  const notificationLinkLabel =
    pendingNotificationsCount > 0
      ? `Notifications, ${pendingNotificationsCount} unread`
      : "Notifications";

  // Handler for login.
  const handleLogin = () => {
    const clientId = process.env.REACT_APP_CLIENT_ID;
    const loginUrl = `${
      process.env.REACT_APP_SERVER_URL
    }/app/login?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      window.location.origin
    )}&original_url=${encodeURIComponent(window.location.href)}`;
    window.location.href = loginUrl;
  };

  // Handler for logout.
  const handleLogoutClick = () => {
    dispatch(logoutUser());
  };

  return (
    <nav className="primaryNavbar navbar navbar-expand-lg navbar-light bg-light" aria-label="Primary navigation">
      <Link className="navbar-brand" to="/" aria-label="SRI Services — home">
        <img src={logo} alt="SRI Services" style={{ marginLeft: "1rem", marginRight: "10px", height: "3rem", width: "auto" }} />
      </Link>

      <button
        className="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarNavAltMarkup"
        aria-controls="navbarNavAltMarkup"
        aria-expanded="false"
        aria-label="Toggle navigation"
      >
        <span className="navbar-toggler-icon"></span>
      </button>

      <div className="collapse navbar-collapse justify-content-end" id="navbarNavAltMarkup">
        <div className="navbar-nav">
          <NavLink className="nav-link" to="/">
            Home
          </NavLink>

          {/* Dropdown for "About SRI" */}
          <NavDropdown className="nav-item dropdown" title="About SRI">
            <NavDropdown.Item className="dropdown-item" as={Link} to="/company">
              Company
            </NavDropdown.Item>
            <NavDropdown.Item className="dropdown-item" as={Link} to="/work">
              How We Work
            </NavDropdown.Item>
            <NavDropdown.Item className="dropdown-item" as={Link} to="/partners">
              Partners
            </NavDropdown.Item>
            <NavDropdown.Item className="dropdown-item" as={Link} to="/investing">
              Investing
            </NavDropdown.Item>
          </NavDropdown>

          <NavLink className="nav-link" to="/support">
            Support
          </NavLink>
          <NavLink className="nav-link" to="/knowledge-base">
            Help
          </NavLink>
          <div className="d-flex saleInformation">
            <NavDropdown className="nav-item dropdown" title="Sale Information">
              <NavDropdown.Item className="dropdown-item nav-link" as={Link} to="/properties">
                Properties
              </NavDropdown.Item>
              <NavDropdown.Item
                as="button"
                type="button"
                className="dropdown-item nav-link"
                onClick={handleOpenAuctionModal}
              >
                Auction List
              </NavDropdown.Item>
            </NavDropdown>
            <div>
              <span className={"badge rounded-pill newBadge"}>New</span>
            </div>
          </div>

          {/* Logged-in links */}
          {isAuthenticated && (
            <NavLink className="nav-link" to="/dashboard">
              Dashboard
            </NavLink>
          )}
          {isAdminOrSriUser ? (
            <a
              className="nav-link"
              href="https://sriservices.atlassian.net/jira/software/c/form/9ca0ca00-0e73-4213-8699-96614e7ad2cd"
              target="_blank"
              rel="noreferrer"
            >
              Submit a Ticket
            </a>
          ) : null}
          {isAuthenticated && (
            <NavLink className="nav-link" to="/profile">
              Profile
            </NavLink>
          )}

          {isAuthenticated ? (
            <>
              <NavLink
                className="nav-link notification-link"
                to="/dashboard#notifications"
                aria-label={notificationLinkLabel}
              >
                <FaBell className="notification-icon" aria-hidden="true" />
                {pendingNotificationsCount > 0 && (
                  <span className="notification-badge" aria-hidden="true">
                    {pendingNotificationsCount}
                  </span>
                )}
              </NavLink>

              {/* Display logged-in email */}
              <span className="navbar-text mx-2">
                <b>{userInfo.user.email || "No Email"}</b>
              </span>

              {/* Logout Button */}
              <button
                type="button"
                className="nav-link buttonStyle"
                onMouseEnter={() => setIsHoveredLogout(true)}
                onMouseLeave={() => setIsHoveredLogout(false)}
                onClick={handleLogoutClick}
              >
                Logout
              </button>
            </>
          ) : (
            // Login / Register Button
            <button
              type="button"
              className="nav-link buttonStyle"
              onMouseEnter={() => setIsHoveredLogin(true)}
              onMouseLeave={() => setIsHoveredLogin(false)}
              onClick={handleLogin}
            >
              Login/Register
            </button>
          )}
        </div>
      </div>

      {/* Auction List Modal */}
      <Modal
        show={showAuctionModal}
        onHide={handleCloseAuctionModal}
        size="xl"
        centered
        dialogClassName="unique-auction-modal"
        aria-labelledby="auction-list-modal"
      >
        <Modal.Header closeButton className="unique-auction-modal-header">
          <Modal.Title id="auction-list-modal">Auction List</Modal.Title>
        </Modal.Header>
        <Modal.Body className="unique-auction-modal-body">
          <AuctionList closeModal={handleCloseAuctionModal} />
        </Modal.Body>
        <Modal.Footer className="unique-auction-modal-footer">
          <Button variant="secondary" onClick={handleCloseAuctionModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </nav>
  );
};

export default Navbar;
